import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Form, Input, Select, Button, Divider, message } from 'antd';
import { SaveOutlined, CloseOutlined, ApartmentOutlined, ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import { designationsApi } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';

const { Option } = Select;

const EditPosition = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/positions', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [positions, setPositions] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    loadPosition();
    loadParents();
  }, [id]);

  const loadPosition = async () => {
    setFetching(true);
    try {
      const res = await designationsApi.getById(id);
      setCurrent(res.data);
      form.setFieldsValue({
        name: res.data?.name,
        parent_id: res.data?.parent_id,
      });
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت اطلاعات سمت');
      goBackToList();
    } finally {
      setFetching(false);
    }
  };

  const loadParents = async () => {
    try {
      const res = await designationsApi.getAll();
      const filtered = (res.data || []).filter((p) => p.id !== parseInt(id, 10));
      setPositions(filtered);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت لیست سمت‌ها');
    }
  };

  const onFinish = async (values) => {
    try {
      const validatedValues = await form.validateFields();
      setLoading(true);
      const payload = {
        name: validatedValues.name,
        parent_id: validatedValues.parent_id || null,
      };
      await designationsApi.update(id, payload);
      message.success('سمت با موفقیت ویرایش شد');
      goBackToList();
    } catch (e) {
      if (e.errorFields && e.errorFields.length > 0) {
        scrollToFirstError(form);
      } else {
        console.error(e);
        message.error(e?.message || 'خطا در ویرایش سمت');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
            <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                  <EditOutlined className="text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش سمت شغلی</h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">در حال بارگذاری...</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
              <FormSkeleton />
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                <EditOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش سمت شغلی</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  {current ? `در حال ویرایش: ${current.name}` : 'ویرایش اطلاعات سمت'}
                </p>
              </div>
            </div>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={goBackToList}
              className="flex items-center gap-2"
              style={{ fontFamily: 'CustomFont' }}
            >
              بازگشت
            </Button>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="border border-gray-100 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800">
              <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError={{ behavior: 'smooth', block: 'center' }} style={{ fontFamily: 'CustomFont' }} size="middle">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    label="نام سمت"
                    name="name"
                    rules={[
                      { required: true, message: 'نام سمت را وارد کنید' },
                      { min: 2, message: 'حداقل 2 کاراکتر' },
                      { max: 100, message: 'حداکثر 100 کاراکتر' },
                    ]}
                  >
                    <Input placeholder="مثال: مدیر فروش" prefix={<ApartmentOutlined className="text-gray-400" />} />
                  </Form.Item>

                  <Form.Item label="سمت والد" name="parent_id">
                    <Select
                      placeholder="انتخاب سمت والد (اختیاری)"
                      allowClear
                      showSearch
                      loading={loading}
                      filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {positions.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <Divider />

                <div className="flex items-center justify-end gap-3">
                  <Button icon={<CloseOutlined />} onClick={goBackToList} style={{ minWidth: '110px' }}>
                    لغو
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    className="bg-blue-600 hover:bg-blue-700 border-0"
                    style={{ minWidth: '140px' }}
                  >
                    ذخیره تغییرات
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditPosition;

